'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import AppLogo from '@/components/ui/AppLogo';
import { setAuthToken, isAuthenticated, getAdminPassword } from '@/lib/dataStore';
import { Eye, EyeOff, Loader2, AlertCircle, Shield } from 'lucide-react';

interface LoginForm {
  username: string;
  password: string;
}

// Backend integration point: Replace with real auth API call
const ADMIN_USERNAME = 'admin';

export default function AdminLoginClient() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  useEffect(() => {
    if (isAuthenticated()) {
      router.replace('/brand-management');
    }
  }, [router]);

  async function onSubmit(data: LoginForm) {
    setIsLoading(true);
    setAuthError('');

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));

    if (data.username === ADMIN_USERNAME && data.password === getAdminPassword()) {
      setAuthToken('mr_admin_session');
      router.push('/brand-management');
    } else {
      setAuthError('Ungültiger Benutzername oder Passwort. Bitte versuchen Sie es erneut.');
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 grid-pattern">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center gap-3 mb-3">
            <AppLogo size={48} />
            <div>
              <div className="font-bold text-foreground text-xl">Media Rabbit</div>
              <div className="text-muted-foreground text-sm">GmbH</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Shield size={14} className="text-primary" />
            <span>Admin-Bereich</span>
          </div>
        </div>

        {/* Login Card */}
        <div className="card-surface p-7 shadow-modal">
          <h1 className="text-xl font-bold text-foreground mb-1">Anmelden</h1>
          <p className="text-muted-foreground text-sm mb-6">Geben Sie Ihre Admin-Zugangsdaten ein.</p>

          {/* Error */}
          {authError && (
            <div className="flex items-start gap-3 bg-danger/10 border border-danger/25 rounded-lg p-3 mb-5">
              <AlertCircle size={16} className="text-danger mt-0.5 flex-shrink-0" />
              <p className="text-danger text-sm">{authError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Benutzername
              </label>
              <input
                type="text"
                className={`input-field ${errors.username ? 'border-danger focus:border-danger' : ''}`}
                placeholder="admin"
                autoComplete="username"
                {...register('username', { required: 'Benutzername ist erforderlich' })}
              />
              {errors.username && (
                <p className="text-danger text-xs mt-1">{errors.username.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Passwort
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`input-field pr-10 ${errors.password ? 'border-danger focus:border-danger' : ''}`}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  {...register('password', { required: 'Passwort ist erforderlich' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-danger text-xs mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 mt-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              style={{ minHeight: '42px' }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Wird angemeldet...</span>
                </>
              ) : (
                <span>Anmelden</span>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-muted-foreground text-xs mt-6">
          Zurück zur{' '}
          <a href="/" className="text-primary hover:underline">
            öffentlichen Seite
          </a>
        </p>
      </div>
    </div>
  );
}